import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LuArrowRight, LuSmartphone, LuRadio, LuShieldCheck, LuDollarSign, LuCheck } from 'react-icons/lu';
import HeaderOne from '../../header/HeaderOne';
import FooterOne from '../../footer/FooterOne';
import Head from '../../components/Head';
import '../landing.css';

const ADVANTAGES = [
  { icon: <LuDollarSign size={16} strokeWidth={2} />, title: 'Lowest price per SMS', body: 'Pay your SIM\'s monthly plan ($5–$25/mo for unlimited) and send all the SMS you want at $0.001 each.' },
  { icon: <LuShieldCheck size={16} strokeWidth={2} />, title: 'No 10DLC, no A2P', body: 'P2P traffic through a real consumer SIM. Skip the 2–6 weeks of carrier registration paperwork.' },
  { icon: <LuRadio size={16} strokeWidth={2} />, title: 'Higher delivery rates', body: 'Real consumer numbers get filtered less than aggregator long codes. Better deliverability, better answer rates.' },
  { icon: <LuSmartphone size={16} strokeWidth={2} />, title: 'Spare phone is enough', body: 'Any Android $60 and up runs the app fine. We have a list of tested cheap phones in our setup guide.' },
];

const STEPS = [
  { n: 1, title: 'Sign up', body: 'Create a TextLink account. 300 free messages, no credit card.' },
  { n: 2, title: 'Install the Android app', body: 'Download our APK on a spare Android phone with a SIM card. Any modern Android works.' },
  { n: 3, title: 'Pair via QR code', body: 'Open Your Devices in the dashboard, scan the QR with your phone. The SIM appears automatically.' },
  { n: 4, title: 'Send your first message', body: 'Use the dashboard, REST API, or GoHighLevel — anything that talks to TextLink now routes through your SIM.' },
];

export default function UseYourOwnNumbers() {
  const navigate = useNavigate();

  return (
    <>
      <Head
        title="Use Your Own SIM — Self-Hosted SMS Gateway"
        description="Turn a spare Android into a self-hosted SMS gateway. Send SMS through your own SIM card at $0.001 per message — no 10DLC, no A2P registration, no aggregator markup. 5-minute setup."
        canonical="/selfhostedservice"
      />
      <HeaderOne />

      <main className="lp">
        <div className="lp-shell">

          <header className="lp-hero">
            <span className="lp-eyebrow">Self-hosted service</span>
            <h1 className="lp-h1">
              Your SIM. Your phone.<br />
              <span className="lp-h1-accent">Your SMS gateway.</span>
            </h1>
            <p className="lp-lead">
              Pair a spare Android phone with your own SIM card and send SMS at $0.001 per message — no aggregator markup, no 10DLC paperwork. Five minutes from signup to first delivered text.
            </p>
            <div className="lp-cta-row">
              <button className="lp-btn lp-btn--primary" onClick={() => navigate('/signup')}>
                Try free <LuArrowRight size={15} strokeWidth={2} />
              </button>
              <Link className="lp-btn lp-btn--ghost" to="/recommended-setup">Recommended SIMs & phones</Link>
            </div>
            <div className="lp-meta">
              <span>500 free messages on signup</span>
              <span className="lp-meta-sep" />
              <span>No credit card</span>
              <span className="lp-meta-sep" />
              <span>Cancel anytime</span>
            </div>
          </header>

          <section className="lp-section">
            <h2 className="lp-h2">Why self-hosted wins</h2>
            <p className="lp-sub">Compared to Twilio, MessageBird, and every aggregator.</p>
            <ul className="lp-feat-list">
              {ADVANTAGES.map((a, i) => (
                <li key={i} className="lp-feat">
                  <div className="lp-feat-icon">{a.icon}</div>
                  <div className="lp-feat-text">
                    <strong>{a.title}</strong>
                    <span>{a.body}</span>
                  </div>
                </li>
              ))}
            </ul>
          </section>

          <section className="lp-section">
            <h2 className="lp-h2">Setup in five minutes</h2>
            <p className="lp-sub">No technical knowledge required. The QR pairing flow handles everything.</p>

            <ol className="lp-steps">
              {STEPS.map((s) => (
                <li key={s.n} className="lp-step">
                  <div className="lp-step-num">{s.n}</div>
                  <div className="lp-step-body">
                    <h3 className="lp-step-title">{s.title}</h3>
                    <p className="lp-step-text">{s.body}</p>
                  </div>
                </li>
              ))}
            </ol>

            <div className="lp-callout">
              <strong>Want a deeper walkthrough?</strong>{' '}
              <Link to="/how-textlink-works" style={{ color: 'inherit', textDecoration: 'underline' }}>How TextLink works</Link>{' '}
              covers the full pipeline. Or jump to{' '}
              <Link to="/recommended-setup" style={{ color: 'inherit', textDecoration: 'underline' }}>recommended SIMs and phones</Link>.
            </div>
          </section>

          <section className="lp-section">
            <h2 className="lp-h2">All TextLink tools work with self-hosted</h2>
            <p className="lp-sub">Same dashboard, same APIs, same inbox.</p>
            <div className="lp-grid-3">
              <Link to="/smsapi" className="lp-card">
                <h3 className="lp-card-h">SMS API</h3>
                <p className="lp-card-p">REST send from any backend.</p>
              </Link>
              <Link to="/bulksms" className="lp-card">
                <h3 className="lp-card-h">No-code Bulk SMS</h3>
                <p className="lp-card-p">CSV upload, personalized template.</p>
              </Link>
              <Link to="/verificationapi" className="lp-card">
                <h3 className="lp-card-h">Verification API</h3>
                <p className="lp-card-p">OTP & 2FA via your SIM.</p>
              </Link>
              <Link to="/bulksmsapi" className="lp-card">
                <h3 className="lp-card-h">Bulk SMS API</h3>
                <p className="lp-card-p">Mass SMS via REST.</p>
              </Link>
              <Link to="/web-sms-app" className="lp-card">
                <h3 className="lp-card-h">Web Texting App</h3>
                <p className="lp-card-p">Browser-based inbox.</p>
              </Link>
              <Link to="/ghl" className="lp-card">
                <h3 className="lp-card-h">GoHighLevel</h3>
                <p className="lp-card-p">Native conversation provider.</p>
              </Link>
            </div>
          </section>

          <section className="lp-cta">
            <h2 className="lp-h2">Use your own SIM, save 90%.</h2>
            <p className="lp-sub">500 free messages on signup. Five minutes from signup to first send.</p>
            <div className="lp-cta-row">
              <button className="lp-btn lp-btn--primary" onClick={() => navigate('/signup')}>
                Try free <LuArrowRight size={15} strokeWidth={2} />
              </button>
              <Link className="lp-btn lp-btn--ghost" to="/pricing">See pricing</Link>
            </div>
          </section>

        </div>
      </main>

      <FooterOne />
    </>
  );
}
